/**
 * Store admin — the storefront appearance library (spec 50 R1).
 *
 * What `/store/admin/storefront` opens on now. Before spec 50 this route dropped
 * straight into the builder canvas, which meant a store had exactly one implicit
 * design and trying a new look destroyed the old one.
 *
 * The screen's one load-bearing claim is that **exactly one appearance is
 * active**. It doesn't enforce that — it can't, and shouldn't: `isActive` is
 * derived server-side from the single `published_home_version_id` pointer
 * (ADR-0063), so at most one card can ever come back active. This just renders
 * what it is told.
 */
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Copy, ExternalLink, MoreHorizontal, Palette, Pencil, Trash2, Check } from "lucide-react";
import {
  Alert,
  Button,
  ConfirmDialog,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  PromptDialog,
  Skeleton,
  StatusPill,
  toast,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { storeErrorMessage } from "../error-message.ts";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { AppearanceThumbnail } from "./appearance-thumbnail.tsx";
import { TemplateGallery } from "./template-gallery.tsx";

type AppearanceRow = {
  id: string;
  name: string;
  templateKey: string | null;
  isActive: boolean;
  sectionCount: number;
  sections: unknown[];
  theme: unknown;
  updatedAt: string;
};

export function AppearanceListPage() {
  const canManage = usePermission("store.catalog.manage");
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const list = trpc.storeAppearances.list.useQuery({});

  const [renaming, setRenaming] = useState<AppearanceRow | null>(null);
  const [archiving, setArchiving] = useState<AppearanceRow | null>(null);
  const [pendingTemplate, setPendingTemplate] = useState<string | null>(null);

  const rows = (list.data?.items ?? []) as AppearanceRow[];
  const storeName = list.data?.storeName ?? "Your store";

  const refresh = () => {
    void utils.storeAppearances.list.invalidate();
    // The editor reads through `storeHome.get`; activating changes which
    // version is "Live" there, so its cache is stale the moment this succeeds.
    void utils.storeHome.get.invalidate();
  };

  const create = trpc.storeAppearances.create.useMutation({
    onSuccess: (res) => {
      setPendingTemplate(null);
      refresh();
      toast.success(`"${res.name}" created`, {
        description: res.activated
          ? "It's live on your storefront — customise it now."
          : "Saved as a draft. Activate it when you're ready.",
      });
      // Straight into the editor, pre-filled — the point of a template (R10).
      void navigate({
        to: "/store/admin/storefront/$appearanceId",
        params: { appearanceId: res.appearanceId },
      });
    },
    onError: (err) => {
      setPendingTemplate(null);
      toast.error("Couldn't create that appearance", { description: storeErrorMessage(err) });
    },
  });

  const activate = trpc.storeAppearances.activate.useMutation({
    onSuccess: () => {
      refresh();
      toast.success("Activated", { description: "Buyers see this within a minute." });
    },
    onError: (err) =>
      toast.error("Couldn't activate", { description: storeErrorMessage(err) }),
  });

  const duplicate = trpc.storeAppearances.duplicate.useMutation({
    onSuccess: (res) => {
      refresh();
      toast.success(`Copied to "${res.name}"`, {
        description: "The copy isn't live — activate it when you're ready.",
      });
    },
    onError: (err) => toast.error("Couldn't duplicate", { description: storeErrorMessage(err) }),
  });

  const rename = trpc.storeAppearances.rename.useMutation();
  const archive = trpc.storeAppearances.archive.useMutation();

  const openEditor = (id: string) =>
    void navigate({
      to: "/store/admin/storefront/$appearanceId",
      params: { appearanceId: id },
    });

  return (
    <StoreAdminLayout
      breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Appearance" }]}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold leading-tight tracking-tight text-ink">Appearance</h1>
          <p className="mt-1.5 text-sm text-muted">
            The looks you've built for your storefront. One is live at a time.
          </p>
        </div>
      </div>

      {list.error ? (
        <Alert intent="bad" title="Couldn't load your appearances" className="mt-4">
          {storeErrorMessage(list.error)}
        </Alert>
      ) : null}

      {list.isLoading ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-64 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        /* R10: the empty state *is* the gallery — never an empty list. */
        <div className="mt-6">
          <TemplateGallery
            variant="empty"
            storeName={storeName}
            canManage={canManage}
            pendingKey={pendingTemplate}
            onPick={(templateKey) => {
              setPendingTemplate(templateKey);
              create.mutate({ templateKey });
            }}
          />
        </div>
      ) : (
        <>
          <ul className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((row) => (
              <li
                key={row.id}
                className={`flex flex-col overflow-hidden rounded border bg-paper shadow ${
                  row.isActive ? "border-accent" : "border-line"
                }`}
              >
                <button
                  type="button"
                  onClick={() => openEditor(row.id)}
                  className="block w-full text-left"
                  aria-label={`Edit ${row.name}`}
                >
                  <AppearanceThumbnail
                    sections={row.sections}
                    theme={row.theme}
                    className="h-40 border-b border-line"
                  />
                </button>

                <div className="flex flex-1 flex-col gap-2 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h2 className="truncate text-sm font-semibold text-ink">{row.name}</h2>
                      <p className="mt-0.5 text-xs text-muted">
                        <span className="font-mono tabular-nums">{row.sectionCount}</span>{" "}
                        {row.sectionCount === 1 ? "section" : "sections"} · updated{" "}
                        {new Date(row.updatedAt).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                        })}
                      </p>
                    </div>
                    {row.isActive ? (
                      <StatusPill tone="ok" size="sm" dot>
                        Live
                      </StatusPill>
                    ) : null}
                  </div>

                  <div className="mt-auto flex items-center gap-2 pt-1">
                    <Button
                      intent="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => openEditor(row.id)}
                    >
                      <Pencil size={13} aria-hidden />
                      Edit
                    </Button>

                    {canManage && !row.isActive ? (
                      <Button
                        intent="primary"
                        size="sm"
                        disabled={activate.isPending}
                        onClick={() => activate.mutate({ appearanceId: row.id })}
                      >
                        <Check size={13} aria-hidden />
                        Activate
                      </Button>
                    ) : null}

                    {canManage ? (
                      <DropdownMenuRoot>
                        <DropdownMenuTrigger asChild>
                          <Button intent="ghost" size="sm" aria-label={`More actions for ${row.name}`}>
                            <MoreHorizontal size={15} aria-hidden />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {/* New-tab preview of the saved design — plain anchor
                              via asChild so the menu item navigates natively. */}
                          <DropdownMenuItem asChild>
                            <a
                              href={`/store/admin/storefront/${row.id}/preview`}
                              target="_blank"
                              rel="noopener"
                            >
                              <ExternalLink size={13} aria-hidden />
                              Live preview
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => setRenaming(row)}>
                            <Pencil size={13} aria-hidden />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={duplicate.isPending}
                            onSelect={() => duplicate.mutate({ appearanceId: row.id })}
                          >
                            <Copy size={13} aria-hidden />
                            Duplicate
                          </DropdownMenuItem>
                          {/* Absent, not disabled-with-a-shrug, on the live one:
                              R7 refuses it server-side and the reason is worth
                              stating rather than hiding behind a dead control. */}
                          <DropdownMenuItem
                            disabled={row.isActive}
                            onSelect={() => {
                              if (!row.isActive) setArchiving(row);
                            }}
                          >
                            <Trash2 size={13} aria-hidden />
                            {row.isActive ? "Archive (activate another first)" : "Archive"}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenuRoot>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {canManage ? (
            <div className="mt-10 border-t border-line pt-6">
              <TemplateGallery
                variant="additional"
                storeName={storeName}
                canManage={canManage}
                pendingKey={pendingTemplate}
                onPick={(templateKey) => {
                  setPendingTemplate(templateKey);
                  create.mutate({ templateKey });
                }}
              />
            </div>
          ) : null}
        </>
      )}

      <PromptDialog
        open={renaming !== null}
        onOpenChange={(open) => {
          if (!open) setRenaming(null);
        }}
        title="Rename appearance"
        description="Only you see this name — it never appears on your storefront."
        label="Name"
        defaultValue={renaming?.name ?? ""}
        confirmLabel="Save"
        pending={rename.isPending}
        onConfirm={async (value) => {
          if (!renaming) return;
          try {
            await rename.mutateAsync({ appearanceId: renaming.id, name: value });
            refresh();
            setRenaming(null);
            toast.success("Renamed");
          } catch (err) {
            // `PromptDialog` swallows the throw to keep the dialog open with the
            // text intact — deliberately, so a duplicate name can be edited
            // rather than retyped. But it documents that the *caller* owns the
            // visible error, so without this the Save button just silently
            // refuses and the merchant is never told why.
            toast.error("Couldn't rename", { description: storeErrorMessage(err) });
            throw err;
          }
        }}
      />

      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(open) => {
          if (!open) setArchiving(null);
        }}
        title="Archive this appearance?"
        description={
          <>
            <strong>{archiving?.name}</strong> comes off your list. Nothing buyers see changes, and
            the design itself is kept — this only tidies the library.
          </>
        }
        confirmLabel="Archive"
        // No `intent="danger"`: every other archive confirm in Store admin
        // (collections, packs, products) uses the default button, and archiving
        // here is reversible — the design is kept, only the list is tidied.
        // The red treatment both broke that consistency and overstated the act.
        pending={archive.isPending}
        onConfirm={async () => {
          if (!archiving) return;
          try {
            await archive.mutateAsync({ appearanceId: archiving.id });
            refresh();
            setArchiving(null);
            toast.success("Archived");
          } catch (err) {
            toast.error("Couldn't archive", { description: storeErrorMessage(err) });
            throw err;
          }
        }}
      />

      {!canManage ? (
        <p className="mt-6 flex items-center gap-1.5 text-xs text-muted">
          <Palette size={13} aria-hidden />
          You can view these but not change them.
        </p>
      ) : null}
    </StoreAdminLayout>
  );
}
